import { z } from "zod";

/**
 * Validation schemas for invitation forms
 *
 * Standards:
 * - Uses Zod for type-safe validation
 * - Consistent error messages
 * - Proper field validation rules
 * - Follows app validation patterns
 */

// Base validation rules - exported for reuse in other schemas
export const firstnameValidation = z
  .string()
  .min(1, "First name is required")
  .min(2, "First name must be at least 2 characters")
  .max(30, "First name must be no more than 30 characters")
  .regex(
    /^[\p{L}\s'.-]+$/u,
    "First name can only contain letters, spaces, hyphens, apostrophes, and periods",
  );

export const lastnameValidation = z
  .string()
  .min(1, "Last name is required")
  .min(2, "Last name must be at least 2 characters")
  .max(30, "Last name must be no more than 30 characters")
  .regex(
    /^[\p{L}\s'.-]+$/u,
    "Last name can only contain letters, spaces, hyphens, apostrophes, and periods",
  );

export const emailValidation = z
  .string()
  .min(1, "Email address is required")
  .email("Please enter a valid email address")
  .max(254, "Email address is too long");

const roleValidation = z.enum([
  "Member",
  "Admin",
  "System User",
  "Superuser",
] as const);

// Permission level validation
const permissionLevelValidation = z.enum([
  "read",
  "edit",
  "manage",
  "none",
] as const);

// Location permissions validation
const locationPermissionsValidation = z.record(
  z.string().uuid("Invalid location ID"),
  permissionLevelValidation,
);

/**
 * Invitation creation form schema
 * Used for validating invitation form data
 */
export const invitationCreateSchema = z.object({
  firstname: firstnameValidation,
  lastname: lastnameValidation,
  email: emailValidation,
  role: roleValidation,
  permissions: locationPermissionsValidation,
});

/**
 * TypeScript type for invitation form data
 */
export type InvitationCreateForm = z.infer<typeof invitationCreateSchema>;

/**
 * Invitation update form schema (PATCH - all fields optional)
 */
export const invitationUpdateSchema = invitationCreateSchema.partial();

export type InvitationUpdateForm = z.infer<typeof invitationUpdateSchema>;

// Individual field validators removed - unused (confirmed via dead code analysis)
// Forms use central Zod schemas with useFormValidation hook instead
